import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Menu, X } from 'lucide-react';
import { useSiteData } from '../context/SiteContext';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data } = useSiteData();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const settings = data?.settings || {
    address: 'г. Красноярск',
    phone: '+7 (391) 272-24-12',
    email: 'info@fipi.pro'
  };

  return (
    <header className="fixed w-full z-50 transition-all duration-300">
      {/* Top Bar - Контакты (всегда темный) */}
      <div className="bg-[#020617] border-b border-white/5 py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-xs font-medium text-slate-400">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 hover:text-accent transition-colors cursor-default">
                <MapPin className="w-3.5 h-3.5" />
                <span>{settings.address}</span>
              </div>
              <a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-accent transition-colors">
                <Mail className="w-3.5 h-3.5" />
                <span>{settings.email}</span>
              </a>
            </div>
            <div className="flex items-center gap-6">
              <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-2 text-slate-200 font-bold hover:text-accent transition-colors">
                <Phone className="w-3.5 h-3.5" />
                <span>{settings.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className={`transition-all duration-300 ${isScrolled ? 'glass-panel py-3 shadow-lg' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-700 flex items-center justify-center text-white font-bold text-xl shadow-lg glow-effect">
                F
              </div>
              <span className="text-2xl font-black tracking-tight text-white">FIPI<span className="text-accent">.PRO</span></span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollTo('features')} className="text-sm font-semibold text-slate-300 hover:text-white transition-colors">Преимущества</button>
              <button onClick={() => scrollTo('pricing')} className="text-sm font-semibold text-slate-300 hover:text-white transition-colors">Тарифы</button>
              <button onClick={() => scrollTo('cases')} className="text-sm font-semibold text-slate-300 hover:text-white transition-colors">Кейсы</button>
              <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="md:hidden lg:block text-sm font-semibold text-slate-300 hover:text-white transition-colors">{settings.phone}</a>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden absolute w-full glass-panel border-b border-white/10 transition-all duration-300 ${mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-4 pt-2 pb-6 space-y-4">
          <button onClick={() => scrollTo('features')} className="block w-full text-left px-4 py-3 text-slate-300 hover:bg-white/5 rounded-lg font-medium">Преимущества</button>
          <button onClick={() => scrollTo('pricing')} className="block w-full text-left px-4 py-3 text-slate-300 hover:bg-white/5 rounded-lg font-medium">Тарифы</button>
          <button onClick={() => scrollTo('cases')} className="block w-full text-left px-4 py-3 text-slate-300 hover:bg-white/5 rounded-lg font-medium">Кейсы</button>
          <div className="pt-4 mt-2 border-t border-white/10 px-4 space-y-4">
            <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-3 text-white font-bold">
              <Phone className="w-5 h-5 text-accent" />
              <span>{settings.phone}</span>
            </a>
            <a href={`mailto:${settings.email}`} className="flex items-center gap-3 text-slate-300">
              <Mail className="w-5 h-5 text-accent" />
              <span>{settings.email}</span>
            </a>
            <div className="flex items-center gap-3 text-slate-300 pb-2">
              <MapPin className="w-5 h-5 text-accent" />
              <span>{settings.address}</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};